import "../../ui/style/override/_pera-wallet-override.scss";

import {
  PERA_SIGN_TXN_MODAL_BODY_STYLES,
  PERA_WALLET_MODAL_BODY_HEADER_CLOSE_BUTTON_STYLES,
  PERA_WALLET_MODAL_BODY_HEADER_STYLES,
  PERA_WALLET_MODAL_BODY_STYLES,
  PERA_WALLET_MODAL_BODY_WRAPPER_STYLES,
  PERA_WALLET_MODAL_STYLES
} from "./PeraConnect.constants";

/**
 * This function is used to setup a MutationObserver to observe the PeraConnect modal.
 * The PeraConnect modal is a shadow DOM element, so we need to use MutationObserver to observe
 * the modal's shadow DOM to detect if the modal is opened.
 */
function setupPeraConnectObserver() {
  const peraWalletRedirectModalObserver = new MutationObserver(() => {
    const peraWalletModal = getPeraWalletModalElement(
      "pera-wallet-redirect-modal-wrapper",
      "pera-wallet-redirect-modal"
    );

    if (peraWalletModal) {
      const peraWalletModalBody = peraWalletModal?.querySelector(
        ".pera-wallet-modal__body"
      ) as HTMLDivElement | undefined;

      Object.assign(peraWalletModal.style, PERA_WALLET_MODAL_STYLES);

      if (peraWalletModalBody) {
        Object.assign(peraWalletModalBody.style, PERA_WALLET_MODAL_BODY_STYLES);

        overridePeraWalletModalBodyHeaderStyles(peraWalletModalBody);
      }
    }
  });

  const peraSignTxnObserver = new MutationObserver(() => {
    const peraWalletModal = getPeraWalletModalElement(
      "pera-wallet-sign-txn-modal-wrapper",
      "pera-wallet-sign-txn-modal"
    );
    const isWrapperElementAlreadyCreated = Boolean(
      peraWalletModal?.querySelector(".pera-wallet-modal-body-wrapper")
    );

    if (peraWalletModal) {
      Object.assign(peraWalletModal.style, PERA_WALLET_MODAL_STYLES);

      const peraWalletModalBody = peraWalletModal?.querySelector(
        ".pera-wallet-modal__body"
      ) as HTMLDivElement | undefined;

      if (peraWalletModalBody) {
        Object.assign(peraWalletModalBody.style, PERA_SIGN_TXN_MODAL_BODY_STYLES);

        overridePeraWalletModalBodyHeaderStyles(peraWalletModalBody);

        if (!isWrapperElementAlreadyCreated) {
          createPeraWalletBodyWrapper(peraWalletModal, peraWalletModalBody);
        }
      }
    }
  });

  const peraConnectObserver = new MutationObserver(() => {
    const peraWalletModal = getPeraWalletModalElement(
      "pera-wallet-connect-modal-wrapper",
      "pera-wallet-connect-modal"
    );

    if (peraWalletModal) {
      const peraWalletModalBody = peraWalletModal?.querySelector(
        ".pera-wallet-modal__body"
      ) as HTMLDivElement | undefined;

      peraWalletModal.classList.replace(
        "pera-wallet-modal--desktop",
        "pera-wallet-modal--mobile"
      );

      Object.assign(peraWalletModal.style, PERA_WALLET_MODAL_STYLES);

      if (peraWalletModalBody) {
        Object.assign(peraWalletModalBody.style, PERA_WALLET_MODAL_BODY_STYLES);

        const peraWalletModalTouchScreenMode = peraWalletModalBody.getElementsByTagName(
          "pera-wallet-modal-touch-screen-mode"
        )?.[0];
        const peraWalletModalDesktopMode = peraWalletModalBody.getElementsByTagName(
          "pera-wallet-modal-desktop-mode"
        )?.[0];

        overridePeraWalletModalBodyHeaderStyles(peraWalletModalBody);

        if (peraWalletModalDesktopMode) {
          const peraWalletConnectModalDesktopMode =
            peraWalletModalDesktopMode.shadowRoot?.querySelector(
              ".pera-wallet-connect-modal-desktop-mode"
            ) as HTMLDivElement | undefined;

          peraWalletConnectModalDesktopMode!.style.gridTemplateColumns = "unset";
          peraWalletConnectModalDesktopMode!.style.gap = "unset";

          const peraWalletModalInfoSection = peraWalletConnectModalDesktopMode!
            .getElementsByTagName("pera-wallet-connect-modal-information-section")?.[0]
            .shadowRoot?.querySelector(
              ".pera-wallet-connect-modal-information-section"
            ) as HTMLElement | undefined;

          if (peraWalletModalInfoSection) {
            peraWalletModalInfoSection.style.display = "none";
          }
        }

        if (peraWalletModalTouchScreenMode) {
          const peraWalletConnectModalTouchScreenMode =
            peraWalletModalTouchScreenMode.shadowRoot?.querySelector(
              ".pera-wallet-connect-modal-touch-screen-mode"
            ) as HTMLDivElement | undefined;

          peraWalletConnectModalTouchScreenMode!.style.padding = "12px";
        }
      }
    }
  });

  peraWalletRedirectModalObserver.disconnect();
  peraWalletRedirectModalObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  peraConnectObserver.disconnect();
  peraConnectObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  peraSignTxnObserver.disconnect();
  peraSignTxnObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

function createPeraWalletBodyWrapper(
  peraWalletModal: HTMLDivElement,
  peraWalletModalBody: HTMLDivElement
) {
  const peraWalletModalBodyWrapper = document.createElement("div");

  peraWalletModalBodyWrapper.classList.add("pera-wallet-modal-body-wrapper");
  Object.assign(peraWalletModalBodyWrapper.style, PERA_WALLET_MODAL_BODY_WRAPPER_STYLES);

  peraWalletModal.appendChild(peraWalletModalBodyWrapper);
  peraWalletModalBodyWrapper.appendChild(peraWalletModalBody);
}

function overridePeraWalletModalBodyHeaderStyles(peraWalletModalBody: HTMLDivElement) {
  const peraWalletModalBodyHeaderShadowRoot = peraWalletModalBody.getElementsByTagName(
    "pera-wallet-modal-header"
  )?.[0];
  const peraWalletModalBodyHeader =
    peraWalletModalBodyHeaderShadowRoot?.shadowRoot?.querySelector(
      ".pera-wallet-modal-header"
    ) as HTMLDivElement | undefined;

  if (peraWalletModalBodyHeader) {
    const peraWalletModalHeaderBrand = peraWalletModalBodyHeader.getElementsByClassName(
      "pera-wallet-modal-header__brand"
    )?.[0] as HTMLDivElement | undefined;
    const peraWalletModalHeaderCloseButton =
      peraWalletModalBodyHeader.getElementsByClassName(
        "pera-wallet-modal-header__close-button"
      )?.[0] as HTMLButtonElement | undefined;

    Object.assign(peraWalletModalBodyHeader.style, PERA_WALLET_MODAL_BODY_HEADER_STYLES);

    if (peraWalletModalHeaderBrand) {
      peraWalletModalHeaderBrand.style.display = "none";
    }

    if (peraWalletModalHeaderCloseButton) {
      Object.assign(
        peraWalletModalHeaderCloseButton.style,
        PERA_WALLET_MODAL_BODY_HEADER_CLOSE_BUTTON_STYLES
      );

      const peraWalletModalHeaderCloseButtonIcon =
        peraWalletModalHeaderCloseButton.getElementsByClassName(
          "pera-wallet-modal-header__close-button__close-icon"
        )?.[0] as HTMLImageElement;

      // Set close icon color to white in all screens
      peraWalletModalHeaderCloseButtonIcon.style.filter = "brightness(0) invert(1)";
    }
  }
}

function getPeraWalletModalElement(wrapperClassName: string, modalTagName: string) {
  const modalRoot = document.getElementById(wrapperClassName);
  const modalShadowRoot = modalRoot?.getElementsByTagName(modalTagName);
  const peraWalletModal = modalShadowRoot?.[0]?.shadowRoot?.querySelector(
    ".pera-wallet-modal"
  ) as HTMLDivElement | undefined;

  return peraWalletModal;
}

export {setupPeraConnectObserver};
